import api from '/src/api/api';
import * as mutypes from "/src/store/mutation.types"
import Vue from "vue";
import {applyEliminationPlaces, mergeEventData, sortTeamsBySlot} from "/src/common/liveEvent";

/**
 * Активные GET /group-match/event/{id}: страница Live смонтирована в нескольких
 * табах сразу, поэтому один и тот же матч могут запросить несколько компонентов.
 * Пока запрос в полёте, все получают один и тот же промис — в сеть уходит один запрос.
 */
const pendingEventRequests = new Map();

const errorMessage = (error) => error?.response?.data?.message;

const toastError = (error) => {
    const message = errorMessage(error);
    if (!message) return;
    Vue.toasted.error(message, {position: 'top-right', duration: 3000});
};

/**
 * Единая точка записи события в стор: перед коммитом выбывшим командам
 * проставляются места (последняя выбывшая — последнее место), а порядок
 * выбывания сохраняется, чтобы ответы бэкенда с place: 0 его не сбрасывали.
 */
const commitEvent = ({commit, getters}, event) => {
    const {event: withPlaces, order} = applyEliminationPlaces(event, getters.getEliminatedTeamIds);

    commit(mutypes.SET_EVENT_SHOW, withPlaces);
    commit(mutypes.SET_TEAMS_FOR_LIVE, withPlaces?.teams ?? []);
    commit(mutypes.SET_ELIMINATED_TEAM_IDS, order);

    return withPlaces;
};

export default {
    state: () => {
        return {
            showEvent: {},
            loadedEventMatchId: null,
            /** id команд в порядке полного выбывания: по нему проставляются места */
            eliminatedTeamIds: [],
            teamsForLive: [],
            currentGroupMatchesBook: [],
            selectedMatchIdBook: null,
            selectedMatchId: null,
            selectedMatchName: '',
            selectedMatchLogin: '',
            selectedMatchPassword: '',
            selectedMatchDate: '',
            matchWithBookmaker: false,
            matchHostCount: 4,
            liveMembersCount: null,
            liveTeamsCount: null,
        }
    },

    mutations: {
        [mutypes.SET_EVENT_SHOW]: (state, value) => state.showEvent = value,
        [mutypes.SET_LOADED_EVENT_MATCH_ID]: (state, value) => state.loadedEventMatchId = value,
        [mutypes.SET_ELIMINATED_TEAM_IDS]: (state, value) => state.eliminatedTeamIds = value,
        [mutypes.SET_TEAMS_FOR_LIVE]: (state, value) => state.teamsForLive = value,
        [mutypes.SET_TOURNAMENT_CURRENT_MATCHES_BOOK]: (state, value) => state.currentGroupMatchesBook = value,
        [mutypes.SET_TOURNAMENT_CURRENT_MATCHES_SELECTED_MATCH_ID_BOOK]: (state, value) => state.selectedMatchIdBook = value,
        [mutypes.SET_SELECTED_MATCH]: (state, value) => state.selectedMatchId = value,
        [mutypes.SET_SELECTED_MATCH_NAME]: (state, value) => state.selectedMatchName = value,
        [mutypes.SET_SELECTED_MATCH_LOGIN]: (state, value) => state.selectedMatchLogin = value,
        [mutypes.SET_SELECTED_MATCH_PASSWORD]: (state, value) => state.selectedMatchPassword = value,
        [mutypes.SET_SELECTED_MATCH_DATE]: (state, value) => state.selectedMatchDate = value,
        [mutypes.SET_MATCH_WITH_BOOKMAKER]: (state, value) => state.matchWithBookmaker = value,
        [mutypes.SET_MATCH_HOST_COUNT]: (state, value) => state.matchHostCount = value,
        [mutypes.SET_MATCH_LIVE_COUNT]: (state, value) => state.liveMembersCount = value,
        [mutypes.SET_MATCH_LIVE_TEAMS_COUNT]: (state, value) => state.liveTeamsCount = value,
    },

    getters: {
        getEventShow: state => state.showEvent,
        getLoadedEventMatchId: state => state.loadedEventMatchId,
        getEliminatedTeamIds: state => state.eliminatedTeamIds,
        getTeamsForLive: state => state.teamsForLive,
        getTournamentCurrentMatchesBook: state => state.currentGroupMatchesBook,
        getTournamentCurrentMatchesSelectedMatchIdBook: state => state.selectedMatchIdBook,
        getSelectedMatch: state => state.selectedMatchId,
        getSelectedMatchName: state => state.selectedMatchName,
        getSelectedMatchLogin: state => state.selectedMatchLogin,
        getSelectedMatchPassword: state => state.selectedMatchPassword,
        getSelectedMatchDate: state => state.selectedMatchDate,
        getMatchWithBookmaker: state => state.matchWithBookmaker,
        getMatchHostCount: state => state.matchHostCount,
        getLiveMembersCount: state => state.liveMembersCount,
        getLiveTeamsCount: state => state.liveTeamsCount,
    },

    actions: {
        async showGroupMatchesBook({commit, getters}, id) {
            let response = await api.book.showMatchesBook(id)
            commit(mutypes.SET_TOURNAMENT_CURRENT_MATCHES_BOOK, response.data.group_matches)
            commit(mutypes.SET_TOURNAMENT_CURRENT_MATCHES_SELECTED_MATCH_ID_BOOK, response.data.group_matches[0]?.id);
            commit(mutypes.SET_SELECTED_MATCH, response.data.group_matches[0]?.id)
            commit(mutypes.SET_SELECTED_MATCH_NAME, response.data.group_matches[0]?.name)
            commit(mutypes.SET_SELECTED_MATCH_LOGIN, response.data.group_matches[0]?.lobby_number)
            commit(mutypes.SET_SELECTED_MATCH_PASSWORD, response.data.group_matches[0]?.password)
            commit(mutypes.SET_SELECTED_MATCH_DATE, response.data.group_matches[0]?.start_time)
            commit(mutypes.SET_MATCH_WITH_BOOKMAKER, response.data.group_matches[0]?.with_bookmaker);
            commit(mutypes.SET_MATCH_HOST_COUNT, response.data.group_matches[0]?.host_count);
        },
        showSelectedMatch({commit, getters}, match) {
            commit(mutypes.SET_SELECTED_MATCH, match.id);
            commit(mutypes.SET_SELECTED_MATCH_NAME, match.name);
            commit(mutypes.SET_SELECTED_MATCH_LOGIN, match.lobby_number);
            commit(mutypes.SET_SELECTED_MATCH_PASSWORD, match.password);
            commit(mutypes.SET_SELECTED_MATCH_DATE, match.start_time);
            commit(mutypes.SET_MATCH_WITH_BOOKMAKER, match.with_bookmaker);
            commit(mutypes.SET_MATCH_HOST_COUNT, match.host_count);

        },
        /**
         * GET /group-match/event/{id}.
         * Одинаковые параллельные запросы схлопываются в один,
         * уже загруженный матч повторно не запрашивается (нужно — params.force).
         */
        async showEventResult({commit, getters}, params) {
            const matchId = (params && typeof params === 'object') ? params.matchId : params;
            const force = (params && typeof params === 'object') ? Boolean(params.force) : false;

            if (matchId === null || matchId === undefined) return getters.getEventShow;

            const key = String(matchId);

            if (pendingEventRequests.has(key)) return pendingEventRequests.get(key);

            if (!force
                && String(getters.getLoadedEventMatchId) === key
                && getters.getEventShow?.teams) {
                return getters.getEventShow;
            }

            const request = api.book.showEvent(matchId)
                .then((response) => {
                    // при смене матча порядок выбывания прошлого матча не нужен
                    if (String(getters.getLoadedEventMatchId) !== key) {
                        commit(mutypes.SET_ELIMINATED_TEAM_IDS, []);
                    }

                    const event = commitEvent({commit, getters}, {
                        ...response.data.event,
                        teams: sortTeamsBySlot(response.data.event?.teams),
                    });

                    commit(mutypes.SET_LOADED_EVENT_MATCH_ID, matchId);

                    return event;
                })
                .finally(() => pendingEventRequests.delete(key));

            pendingEventRequests.set(key, request);

            return request;
        },
        /** @deprecated сортировка по слотам теперь всегда в showEventResult */
        async showEventResultWithSlotSort({dispatch}, matchId) {
            return dispatch('showEventResult', {matchId, force: true});
        },
        /**
         * Обновляет уже загруженное событие данными из ответа записывающего запроса,
         * не перезагружая страницу: меняется только пришедшая информация,
         * команды других хостов и порядок отображения сохраняются.
         */
        applyEventFromResponse({commit, getters}, event) {
            if (!event) return getters.getEventShow;

            return commitEvent({commit, getters}, mergeEventData(getters.getEventShow, event));
        },
        /** Статус матча меняется на месте — страница не перезагружается. */
        setEventType({commit, getters}, type) {
            const event = getters.getEventShow;
            if (!event || !type || event.type === type) return event;

            const next = {...event, type};
            commit(mutypes.SET_EVENT_SHOW, next);

            return next;
        },
        async showMembersTournamentCount({commit, getters}) {
            let response = await api.book.showEventTournamentStatus(getters.getSelectedMatch);
            commit(mutypes.SET_MATCH_LIVE_COUNT, response.data.live_members_count);
            commit(mutypes.SET_MATCH_LIVE_TEAMS_COUNT, response.data.live_teams_count);
        },

        async storeRate({getters, dispatch}, params) {
            // let allRates = [];
            // getters.getTeamsForLive.forEach((e) => {
            //         if (e.rate != 0) {
            //             allRates.push(e.rate);
            //         }
            //     }
            // )
            //
            // allRates = [...new Set(allRates)]

            // if (allRates.length < getters.getTeamsForLive.length){
            //     this._vm.$toast.error("У одной или нескольких команд неуникальный rate");
            //     return;
            // }


            try {
                await api.book.storeRate(params.matchId, {
                    type: params.type,
                    actions: getters.getTeamsForLive
                });

                // состояние раздела подтягиваем сразу, без ручного обновления страницы
                await dispatch('showEventResult', {matchId: params.matchId, force: true});
                // статус ставим после перезагрузки, иначе ответ GET его перетрёт
                dispatch('setEventType', 'not_started');
                this._vm.$toast.success("Rate добавлен");
            } catch (error) {
                toastError(error);
            }
        },

        /** Ручная правка места: побеждает автоматическую простановку по выбыванию. */
        async storePlace({commit, getters}, params) {
            const event = getters.getEventShow;
            const teams = (event?.teams ?? []).map((team) => (
                String(team.team_id) === String(params.teamId)
                    ? {...team, place: params.place}
                    : team
            ));

            commit(mutypes.SET_EVENT_SHOW, {...event, teams});
            commit(mutypes.SET_TEAMS_FOR_LIVE, teams);
            // команда выходит из очереди выбывания, иначе пересчёт затрёт ручное место
            commit(
                mutypes.SET_ELIMINATED_TEAM_IDS,
                getters.getEliminatedTeamIds.filter((id) => String(id) !== String(params.teamId))
            );


            // временно убираем
            // await api.book.storeRate(params.matchId, {
            //     type: params.type,
            //     actions: teams
            // })
            //     .then((res) => {
            //         dispatch('showEventResult', params.matchId);
            //         this._vm.$toast("place изменен");
            //
            //     })
            //     .catch((res) => {
            //         this._vm.$toast.error("Необходимо заполнить place");
            //     });
        },


        /** Live / Пауза. Статус и данные команд обновляются сразу из ответа бэкенда. */
        async storeLive({getters, dispatch}, params) {
            const actions = getters.getTeamsForLive.map((team) => ({team_id: team.team_id, rate: team.rate}));

            try {
                const res = await api.book.storeRate(params.matchId, {
                    type: params.type,
                    actions
                });

                dispatch('applyEventFromResponse', res?.data?.event);
                dispatch('setEventType', params.type || 'live');
            } catch (error) {
                toastError(error);
            }
        },
        /** Завершить / Переигровка. Места уходят на бэкенд, статус обновляется на месте. */
        async storeEnd({getters, dispatch}, params) {
            const actions = (getters.getEventShow?.teams ?? [])
                .map((team) => ({team_id: team.team_id, rate: team.rate, place: team.place}));

            try {
                const res = await api.book.storeRate(params.matchId, {
                    type: params.type,
                    actions
                });

                dispatch('applyEventFromResponse', res?.data?.event);
                dispatch('setEventType', params.type || 'ended');
            } catch (error) {
                toastError(error);
            }
        },
        async addRate({commit, getters}, params) {
            const teams = getters.getTeamsForLive;
            const filterTeams = teams.filter((team) => Number.parseInt(team.team_id) !== Number.parseInt(params.team_id));
            filterTeams.push(params)
            commit(mutypes.SET_TEAMS_FOR_LIVE, filterTeams);
        },

        /**
         * POST /group-match/event/{id}/change-user.
         * Отправляет результаты сразу всех игроков команды (params.actions).
         * Ответ не вызывает повторный GET события: данные берём из response.
         */
        async storeKill({getters, dispatch}, params) {
            const actions = params.actions
                ?? [{user_id: params.user_id, kill: params.kill, death: params.death}];

            try {
                const res = await api.book.storeKill(params.matchId, {
                    token: params.token,
                    actions,
                });

                dispatch('applyEventFromResponse', res.data?.event);

                // счётчик обновляем только если он уже открыт пользователем
                if (getters.getLiveMembersCount !== null) {
                    dispatch('showMembersTournamentCount');
                }

                return res;
            } catch (error) {
                toastError(error);
                throw error;
            }
        },

        async storeAllTeamsWithDeath({getters, dispatch}, params) {
            const liveTeam = getters.getEventShow?.teams?.find((team) => {
                return String(team.team_id) === String(params.teamId);
            });

            if (!liveTeam) return null;

            const sendTeamToDeath = liveTeam.members.map((member) => {
                return {
                    user_id: member.user_id,
                    // kill: member.kills,
                    kill: 0,
                    death: 1
                }
            })

            return dispatch('storeKill', {
                matchId: params.matchId,
                token: params.token,
                actions: sendTeamToDeath,
            });
        },
        async storeMatch({commit, getters, dispatch}, params) {
            await api.book.storeMatch({
                group_matches: [{
                    name: params.name,
                    group_id: params.group_id,
                    lobby_number: params.lobby_number,
                    password: params.password,
                    start_time: params.start_time,
                    status: params.status,
                    host_count: params.host_count,
                    with_bookmaker: params.with_bookmaker
                }]
            })
                .then((res) => {
                    const matches = getters.getTournamentCurrentMatchesBook
                    commit(mutypes.SET_TOURNAMENT_CURRENT_MATCHES_BOOK, matches);
                    dispatch('showGroupMatchesBook', params.group_id);
                    this._vm.$toast.success("Матч добавлен");
                })
                .catch((error) => {
                    this._vm.$toast.error(error.response.data.message);
                })
        },
        async deleteMatch({commit, getters}, params) {
            await api.book.deleteMatch(params.matchId);
            let matchValue = getters.getTournamentCurrentMatchesBook.filter((el) => el.id !== params.matchId)
            commit(mutypes.SET_TOURNAMENT_CURRENT_MATCHES_BOOK, matchValue)
        },
        async updateMatchBook({commit, getters, dispatch}, params) {
            await api.book.updateMatch({
                group_matches: [{
                    id: params.matchId,
                    name: params.name,
                    group_id: params.group_id,
                    lobby_number: params.lobby_number,
                    password: params.password,
                    start_time: params.start_time,
                    status: params.status,
                    host_count: params.host_count,
                    with_bookmaker: params.with_bookmaker
                }]
            })
                .then((res) => {
                    const matches = getters.getTournamentCurrentMatchesBook
                    commit(mutypes.SET_TOURNAMENT_CURRENT_MATCHES_BOOK, matches);
                    dispatch('showGroupMatchesBook', params.group_id);
                    this._vm.$toast.success("Матч обновлен");
                })
                .catch((error) => {
                    this._vm.$toast.error(error.response.data.message);
                })

        },
    }
}